/**
 * Linear hierarchy for the role-ordered gates (hasRole / requireXOrAbove).
 *
 * Since #2103 roles come from the person's Ayrical membership. `technician`
 * and `lead_tech` are both "Tech" (lead tech is retired; every tech keeps its
 * capabilities), so they share a rank. `viewer` is read-only and sits below
 * Tech, so it holds no min-role capability. `sales` and `finance` share the
 * Tech rank for the generic "X or above" gates; their manager-level powers
 * (financial visibility, estimate posting) are granted explicitly in
 * ROLE_CAPABILITIES below, NOT by this integer.
 */
export const ROLE_HIERARCHY = {
    viewer: 0,
    technician: 1,
    lead_tech: 1,
    sales: 1,
    finance: 1,
    manager: 2,
    admin: 3,
};
export const ROLE_LABELS = {
    viewer: 'Viewer',
    technician: 'Tech',
    lead_tech: 'Tech',
    sales: 'Sales',
    finance: 'Finance',
    manager: 'Manager',
    admin: 'Admin',
};
/** Where roles and access are managed since #2103 (ADR 0012). */
export const AYRICAL_PEOPLE_ADMIN_URL = 'https://ayrical.com/alturex/admin/people';
/**
 * Canonical capability ID → minimum role required.
 *
 * The backend mirrors this table in `backend/src/middleware/auth.js`
 * because the backend isn't a pnpm workspace member and can't import
 * the shared package today (file: dep blocked by the docker context;
 * the shared dist also emits extension-less ESM imports). A parity
 * test in `backend/__tests__/role-policy-parity.test.js` enforces the
 * two copies stay in sync.
 */
export const CAPABILITY_MIN_ROLE = {
    workspace_edit: 'technician',
    // #577 policy: technicians adjust their own catalogs — nicknames, internal
    // notes, and team favorites all ride this capability. Vendor/private-item
    // data does NOT: those routes moved to `sales_view` (sales/manager/admin).
    catalog_edit_nicknames: 'technician',
    // Any authenticated member can add or replace a catalog item's photo (#558);
    // 'technician' is the lowest rank, so every role gets it. Backend mirror keeps
    // the matching comment. The server-side pipeline still strips EXIF/GPS.
    catalog_manage_thumbnails: 'technician',
    catalog_view_stats: 'lead_tech',
    catalog_update_qbo: 'manager',
    customer_create: 'manager',
    customer_tax_qbo_writeback: 'manager',
    kits_manage: 'lead_tech',
    team_view: 'manager',
    // See prices (rates, values, price/cost history, reports) → canViewRates.
    // Split from financials_view by #2103: seeing money is not sending to QBO.
    prices_view: 'manager',
    // Money actions on field tickets (send to QBO, recall, refresh paid,
    // estimate send) → canPushToQbo.
    financials_view: 'manager',
    review_queue_view: 'manager',
    review_approve: 'manager',
    // Billing-review resolution + manual invoice-number assignment (#802). A money
    // workflow, not a review approval: Sales gets it explicitly in
    // ROLE_CAPABILITIES, finance does not (it exports approved tickets; it does
    // not decide how work is billed).
    billing_resolution: 'manager',
    // Ticket-linked correction/regroup only. Sales gets the same narrow grant in
    // ROLE_CAPABILITIES without receiving blanket invoice management.
    ticket_qbo_resync: 'manager',
    // Permanent removal is narrower than ordinary workspace editing and is
    // intentionally absent from the Sales/Finance explicit capability grants.
    ticket_hard_delete: 'manager',
    // Archive a ticket in any status (manual archive, DELETE /tickets/:id).
    // The app's handler lets every member archive draft/field_review and only
    // managers/admins the rest; MCP agents (Ayrical #2256) must hold this for
    // every archive. Absent from the Sales/Finance explicit grants.
    ticket_archive_any: 'manager',
    users_manage: 'admin',
    // Invite, change roles, suspend/reactivate members through Ayrical (#2103).
    // Manager+ via the ladder; Sales explicitly (it grants Sales), Finance
    // explicitly not. Ayrical's grant rules decide which roles and members.
    members_manage: 'manager',
    // Vendor management + purchase orders (#546 Phase 1). Manager+ via the ladder;
    // Sales gets it explicitly in ROLE_CAPABILITIES (the ladder can't express
    // "sales yes, lead_tech no"). Backend mirror keeps the matching entry.
    po_manage: 'manager',
    // PO push is the sales-surface QBO write, same cohort as po_manage but a
    // separate gate so a future change can split local PO drafting from QBO writes.
    po_qbo_write: 'manager',
    // PO ↔ site linking (#612, epic #609). Same cohort as po_manage — the link
    // panels expose PO cost. Sales explicit in ROLE_CAPABILITIES; backend mirror
    // keeps the matching entries.
    po_link: 'manager',
    // Customer-issued PO/authorization numbers (#615). Manager+ via the ladder;
    // Sales gets it explicitly in ROLE_CAPABILITIES.
    customer_po_manage: 'manager',
    // Inventory (#546 Phase 2, signed-off §4 matrix). view + receive are open to
    // every role ('technician' min-role — techs are at the shelf); config
    // (track_inventory toggle) is Manager+ catalog config. Backend mirror keeps
    // the matching entries + the sales-explicit grants in resolveCapabilities.
    // inventory_adjust (manual counts) is Phase 3 — deliberately absent.
    inventory_view: 'technician',
    inventory_receive: 'technician',
    inventory_config: 'manager',
};
const DEFAULT_CAPABILITIES = {
    canEditWorkspace: false,
    canViewRates: false,
    canEditNicknames: false,
    canManageKits: false,
    canManageThumbnails: false,
    canViewCatalogStats: false,
    canUpdateQboItems: false,
    canPushToQbo: false,
    canViewReviewQueue: false,
    canApproveTickets: false,
    canResolveBilling: false,
    canResyncQboTickets: false,
    canHardDeleteTickets: false,
    canViewTeam: false,
    canManageUsers: false,
    canManageMembers: false,
    canCreateCustomer: false,
    canWriteCustomerTaxToQbo: false,
    canViewSales: false,
    canManageInvoices: false,
    canCreateInvoices: false,
    canConvertEstimate: false,
    canManageBillingMode: false,
    canManagePurchaseOrders: false,
    canLinkPurchaseOrders: false,
    canManageCustomerPos: false,
    canPushPurchaseOrders: false,
    canViewInventory: false,
    canReceiveInventory: false,
    canConfigInventory: false,
};
/**
 * Explicit per-role capability map — the source of truth for what each role can
 * do. Replaces the old hasRole()-derived capabilities so a role like `sales`
 * (manager-level financials, no team/admin) can be expressed; the linear
 * hierarchy alone cannot. The four canonical rows reproduce the previous
 * hierarchy-derived behavior exactly (enforced by permissions.test.ts).
 *
 * Backend mirror: backend/src/middleware/auth.js resolveCapabilities (snake_case
 * IDs). Keep the two in sync.
 */
const TECH_CAPABILITIES = {
    ...DEFAULT_CAPABILITIES,
    canEditWorkspace: true,
    // #577: techs adjust their catalogs — nicknames/notes/favorites.
    canEditNicknames: true,
    // Former lead-tech powers; every Tech holds them since #2103.
    canManageKits: true,
    // #558: any role, down to a field technician, can add/replace item photos.
    canManageThumbnails: true,
    canViewCatalogStats: true,
    // #546 Phase 2: on-hand visibility + receiving are open to every Tech.
    canViewInventory: true,
    canReceiveInventory: true,
};
export const ROLE_CAPABILITIES = {
    // Read-only (#2103): views boards, sites and tickets; no edits, prices or
    // money actions.
    viewer: { ...DEFAULT_CAPABILITIES },
    // Lead tech is retired: both stored values are "Tech" with one capability set.
    technician: { ...TECH_CAPABILITIES },
    lead_tech: { ...TECH_CAPABILITIES },
    sales: {
        ...DEFAULT_CAPABILITIES,
        canEditWorkspace: true,
        canViewRates: true,
        canEditNicknames: true,
        canManageThumbnails: true,
        canViewCatalogStats: true,
        canPushToQbo: true,
        // Same money workflow Sales already owns via push/recall (#802).
        canResolveBilling: true,
        canResyncQboTickets: true,
        canCreateCustomer: true,
        canWriteCustomerTaxToQbo: true,
        canViewSales: true,
        // #870: everyone who can see the Sales page can create invoices — the
        // create-only slice; editing billed invoices stays canManageInvoices.
        canCreateInvoices: true,
        canConvertEstimate: true,
        canManagePurchaseOrders: true,
        canLinkPurchaseOrders: true,
        canManageCustomerPos: true,
        canPushPurchaseOrders: true,
        canViewInventory: true,
        canReceiveInventory: true,
        // #2103: Sales invites and manages Sales members (Ayrical's grant rule).
        canManageMembers: true,
    },
    // #808 relaxed #802's read-only boundary: finance edits the workspace like a
    // technician. Everything it still cannot do (approve, resolve billing, manage
    // invoices, Sales/Purchasing/Team/Admin) stays denied below via DEFAULT.
    finance: {
        ...DEFAULT_CAPABILITIES,
        canEditWorkspace: true,
        canViewRates: true,
        canPushToQbo: true,
        canViewReviewQueue: true,
        canWriteCustomerTaxToQbo: true,
        // #871: Finance marks POs received (packing-slip requirement waived, like
        // Sales/Manager/Admin). App-local inventory only — never QBO.
        canReceiveInventory: true,
        // #2103: Finance grants no role in Ayrical, so it manages no members.
        canManageMembers: false,
    },
    manager: {
        ...DEFAULT_CAPABILITIES,
        canEditWorkspace: true,
        canViewRates: true,
        canEditNicknames: true,
        canManageKits: true,
        canManageThumbnails: true,
        canViewCatalogStats: true,
        canUpdateQboItems: true,
        canPushToQbo: true,
        canViewReviewQueue: true,
        canApproveTickets: true,
        canResolveBilling: true,
        canResyncQboTickets: true,
        canHardDeleteTickets: true,
        canViewTeam: true,
        canManageMembers: true,
        canCreateCustomer: true,
        canWriteCustomerTaxToQbo: true,
        canViewSales: true,
        canManageInvoices: true,
        canCreateInvoices: true,
        canConvertEstimate: true,
        canManageBillingMode: true,
        canManagePurchaseOrders: true,
        canLinkPurchaseOrders: true,
        canManageCustomerPos: true,
        canPushPurchaseOrders: true,
        canViewInventory: true,
        canReceiveInventory: true,
        canConfigInventory: true,
    },
    admin: {
        ...DEFAULT_CAPABILITIES,
        canEditWorkspace: true,
        canViewRates: true,
        canEditNicknames: true,
        canManageKits: true,
        canManageThumbnails: true,
        canViewCatalogStats: true,
        canUpdateQboItems: true,
        canPushToQbo: true,
        canViewReviewQueue: true,
        canApproveTickets: true,
        canResolveBilling: true,
        canResyncQboTickets: true,
        canHardDeleteTickets: true,
        canViewTeam: true,
        canCreateCustomer: true,
        canViewSales: true,
        canManageUsers: true,
        canManageMembers: true,
        canWriteCustomerTaxToQbo: true,
        canManageInvoices: true,
        canCreateInvoices: true,
        canConvertEstimate: true,
        canManageBillingMode: true,
        canManagePurchaseOrders: true,
        canLinkPurchaseOrders: true,
        canManageCustomerPos: true,
        canPushPurchaseOrders: true,
        canViewInventory: true,
        canReceiveInventory: true,
        canConfigInventory: true,
    },
};
export function getUserRole(user) {
    if (user.role)
        return user.role in ROLE_HIERARCHY ? user.role : 'viewer';
    if (user.isAdmin)
        return 'admin';
    if (user.isManager)
        return 'manager';
    if (user.isLeadTech)
        return 'lead_tech';
    return 'technician';
}
export function hasRole(user, minRole) {
    const userRole = getUserRole(user);
    return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[minRole];
}
export function getUserCapabilities(user) {
    const role = getUserRole(user);
    const derived = { ...(ROLE_CAPABILITIES[role] ?? DEFAULT_CAPABILITIES) };
    if (!user.capabilities) {
        return derived;
    }
    // Auth responses carry backend capability IDs in snake_case. Most client
    // checks still derive from role, but permanent deletion is intentionally
    // wired to the explicit server payload as well so a server-side false can
    // disable the destructive affordance without widening any role.
    const serverCapabilities = user.capabilities;
    // Backward compatible bridge: prefer explicit backend capabilities when present.
    const merged = {
        ...DEFAULT_CAPABILITIES,
        ...derived,
        ...user.capabilities,
        ...(typeof serverCapabilities.ticket_hard_delete === 'boolean'
            ? { canHardDeleteTickets: serverCapabilities.ticket_hard_delete }
            : {}),
    };
    return {
        ...merged,
        // Financial/QBO permissions cannot be elevated by a stale or malformed
        // capability payload. Explicit false values may still disable managers.
        canViewRates: derived.canViewRates && merged.canViewRates,
        canUpdateQboItems: derived.canUpdateQboItems && merged.canUpdateQboItems,
        canPushToQbo: derived.canPushToQbo && merged.canPushToQbo,
        canViewReviewQueue: derived.canViewReviewQueue && merged.canViewReviewQueue,
        canApproveTickets: derived.canApproveTickets && merged.canApproveTickets,
        // Resolving billing reviews and stamping manual invoice numbers decides how
        // work gets billed; a stale payload must never elevate a role into it.
        canResolveBilling: derived.canResolveBilling && merged.canResolveBilling,
        canEditWorkspace: derived.canEditWorkspace && merged.canEditWorkspace,
        canResyncQboTickets: derived.canResyncQboTickets && merged.canResyncQboTickets,
        // Permanent deletion is destructive; never let a malformed capability
        // payload elevate a non-manager role into it.
        canHardDeleteTickets: derived.canHardDeleteTickets && merged.canHardDeleteTickets,
        // Creating a QBO customer is a real external write; a stale/malformed
        // payload must never elevate a technician/lead_tech into it.
        canCreateCustomer: derived.canCreateCustomer && merged.canCreateCustomer,
        canWriteCustomerTaxToQbo: derived.canWriteCustomerTaxToQbo && merged.canWriteCustomerTaxToQbo,
        // Invoice editing rewrites billed records; a malformed payload must never
        // elevate a non-manager into it.
        canManageInvoices: derived.canManageInvoices && merged.canManageInvoices,
        // Invoice creation is a real QBO write (#870); a stale/malformed payload
        // must never elevate a technician/lead_tech/finance into it.
        canCreateInvoices: derived.canCreateInvoices && merged.canCreateInvoices,
        // Estimate→invoice conversion is a real QBO write; a stale/malformed payload
        // must never elevate a technician/lead_tech into it.
        canConvertEstimate: derived.canConvertEstimate && merged.canConvertEstimate,
        // Flipping org-wide billing mode moves money behavior; never let a malformed
        // payload elevate a non-manager into it.
        canManageBillingMode: derived.canManageBillingMode && merged.canManageBillingMode,
        // POs carry vendor cost (financial data); a stale/malformed payload must
        // never elevate a technician/lead_tech into the PO module.
        canManagePurchaseOrders: derived.canManagePurchaseOrders && merged.canManagePurchaseOrders,
        // PO↔site linking sits on the same cost-bearing surface (#612).
        canLinkPurchaseOrders: derived.canLinkPurchaseOrders && merged.canLinkPurchaseOrders,
        // Customer PO mutations are a Sales/Manager/Admin accounting workflow; a
        // stale payload must never elevate technician/lead_tech into it.
        canManageCustomerPos: derived.canManageCustomerPos && merged.canManageCustomerPos,
        // PO send-to-QBO is a real external QBO write; never let a stale/malformed
        // payload elevate a technician/lead_tech into it.
        canPushPurchaseOrders: derived.canPushPurchaseOrders && merged.canPushPurchaseOrders,
        // Member management changes who can sign in; a stale payload must never
        // open it to a role Ayrical gives no grant rights (Finance/Tech/Viewer).
        canManageMembers: derived.canManageMembers && merged.canManageMembers,
    };
}
/** Permission checks */
export const permissions = {
    canEditWorkspace: (user) => getUserCapabilities(user).canEditWorkspace,
    canViewRates: (user) => getUserCapabilities(user).canViewRates,
    canEditNicknames: (user) => getUserCapabilities(user).canEditNicknames,
    canManageKits: (user) => getUserCapabilities(user).canManageKits,
    canManageThumbnails: (user) => getUserCapabilities(user).canManageThumbnails,
    canViewCatalogStats: (user) => getUserCapabilities(user).canViewCatalogStats,
    canUpdateQboItems: (user) => getUserCapabilities(user).canUpdateQboItems,
    canPushToQbo: (user) => getUserCapabilities(user).canPushToQbo,
    canViewReviewQueue: (user) => getUserCapabilities(user).canViewReviewQueue,
    canApproveTickets: (user) => getUserCapabilities(user).canApproveTickets,
    canResolveBilling: (user) => getUserCapabilities(user).canResolveBilling,
    canHardDeleteTickets: (user) => getUserCapabilities(user).canHardDeleteTickets,
    canViewTeam: (user) => getUserCapabilities(user).canViewTeam,
    canManageUsers: (user) => getUserCapabilities(user).canManageUsers,
    canManageMembers: (user) => getUserCapabilities(user).canManageMembers,
    canCreateCustomer: (user) => getUserCapabilities(user).canCreateCustomer,
    canWriteCustomerTaxToQbo: (user) => getUserCapabilities(user).canWriteCustomerTaxToQbo,
    canViewSales: (user) => getUserCapabilities(user).canViewSales,
    canManageInvoices: (user) => getUserCapabilities(user).canManageInvoices,
    canCreateInvoices: (user) => getUserCapabilities(user).canCreateInvoices,
    canConvertEstimate: (user) => getUserCapabilities(user).canConvertEstimate,
    canManageBillingMode: (user) => getUserCapabilities(user).canManageBillingMode,
    canManagePurchaseOrders: (user) => getUserCapabilities(user).canManagePurchaseOrders,
    canLinkPurchaseOrders: (user) => getUserCapabilities(user).canLinkPurchaseOrders,
    canManageCustomerPos: (user) => getUserCapabilities(user).canManageCustomerPos,
    canPushPurchaseOrders: (user) => getUserCapabilities(user).canPushPurchaseOrders,
    canViewInventory: (user) => getUserCapabilities(user).canViewInventory,
    canReceiveInventory: (user) => getUserCapabilities(user).canReceiveInventory,
    canConfigInventory: (user) => getUserCapabilities(user).canConfigInventory,
};
//# sourceMappingURL=roles.js.map